import { VehiclePluginsSelect } from "@/components/data-driven-inputs/selects/vehicle-plugins-select/VehiclePluginsSelect";
import { NumericAgrVehicleWeightTextInput } from "@/components/numeric-agr-vehicle-weight-text-input/NumericAgrVehicleWeightTextInput";
import { NumericTextInput } from "@/components/numeric-text-input/NumericTextInput";
import { dotToCommaTransform } from "@/lib/formatters/NumericTextFieldInputCleaner";
import { FormModelChangeHandler } from "@/types/forms/types";
import { SpaceValues } from "@/lib/utils/SpaceValues";
import {
  PriceCalculatorFormData,
  PriceCalculatorFormDataDefaultState,
} from "@/features/price-calculator/types/types";
import { Box, Heading, Row, Space } from "@stenajs-webui/core";
import * as React from "react";
import { FieldCaption } from "@/components/forms/FieldCaption";
import { PriceCalculatorVehicleSelect } from "./PriceCalculatorVehicleSelect";
import { GapValues } from "@/lib/utils/GapValues";
import { useMemo } from "react";
import { NumericTextInputNoScroll } from "@/components/forms/NumericTextInputNoScroll";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";

interface Props {
  formData: PriceCalculatorFormData;
  onChangeFormData: FormModelChangeHandler<PriceCalculatorFormData>;
  setDefaultFormState: React.Dispatch<
    React.SetStateAction<PriceCalculatorFormDataDefaultState>
  >;
  defaultFormState: PriceCalculatorFormDataDefaultState;
}

export const PriceCalculatorVehicleDetails: React.FC<Props> = ({
  formData,
  onChangeFormData,
  setDefaultFormState,
  defaultFormState,
}) => {
  const tradeVehicleSelected = useMemo(() => {
    return /([T][1-9])|([U][1-9])/.test(formData.vehicleType ?? "");
  }, [formData.vehicleType]);

  const palletUnitsVehicleSelected = useMemo(() => {
    return /[G][1-9]/.test(formData.vehicleType ?? "");
  }, [formData.vehicleType]);

  return (
    <Box>
      <Heading variant={"h4"}>Vehicle details</Heading>
      <Space num={SpaceValues.SIXTEEN} />
      <Box gap={GapValues.TWENTYFOUR}>
        <Row gap={GapValues.TWENTYFOUR}>
          <FieldCaption label={"Vehicle type"} width={190}>
            <div
              data-testId={
                testIdConstants.priceCalculatorMultilegVehicleTypeSelect
              }
            >
              <PriceCalculatorVehicleSelect
                formData={formData}
                onChangeFormData={onChangeFormData}
                setDefaultFormState={setDefaultFormState}
                defaultFormState={defaultFormState}
              />
            </div>
          </FieldCaption>
          <FieldCaption label={"Length (m)"} width={60}>
            <NumericTextInput
              value={dotToCommaTransform(formData.vehicleLength ?? "")}
              onValueChange={(value) =>
                onChangeFormData("vehicleLength", value)
              }
              maxNumbers={2}
              maxDecimals={1}
            />
          </FieldCaption>
          <FieldCaption label={"Width (m)"} width={60}>
            <NumericTextInput
              value={dotToCommaTransform(formData.vehicleWidth ?? "")}
              onValueChange={(width) => onChangeFormData("vehicleWidth", width)}
              maxNumbers={2}
              maxDecimals={1}
            />
          </FieldCaption>
          <FieldCaption label={"Weight (kg)"} width={75}>
            <NumericAgrVehicleWeightTextInput
              value={formData.vehicleWeight}
              onValueChange={(weight) =>
                onChangeFormData("vehicleWeight", weight)
              }
            />
          </FieldCaption>
          <FieldCaption label={"Plugins"} width={85}>
            <VehiclePluginsSelect
              value={formData.vehiclePlugins}
              onValueChange={(plugins) =>
                onChangeFormData("vehiclePlugins", plugins)
              }
            />
          </FieldCaption>
        </Row>
        {tradeVehicleSelected && (
          <FieldCaption label={"Trade vehicle weight (kg)"} width={100}>
            <NumericAgrVehicleWeightTextInput
              value={String(formData.vehicleTradeWeight)}
              onValueChange={(tradeWeight) =>
                onChangeFormData("vehicleTradeWeight", tradeWeight)
              }
            />
          </FieldCaption>
        )}
        {palletUnitsVehicleSelected && (
          <FieldCaption label={"No. of units"} width={75}>
            <NumericTextInputNoScroll
              value={formData.vehicleTradeUnits?.toString()}
              onValueChange={(tradeUnits) =>
                onChangeFormData("vehicleTradeUnits", parseFloat(tradeUnits))
              }
              min={0}
            />
          </FieldCaption>
        )}
      </Box>
    </Box>
  );
};
